import type { TransferListItem, TransferProps, TransferAllowedId } from '@ibsys/kit';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AwaitContainer, Button, Spinner, Transfer, Tooltip } from '@ibsys/kit';
import { RotateCcwIcon } from 'lucide-react';
import { ConversationAttributeItem } from '@/entities/attributes';
import { NO_GROUP_KEY } from './constants';
import { useConversationsAttributes } from '@/entities/conversations';
import { cn } from '@/shared/lib';
import { useMainLayout } from '@/shared/ui';
import { ColumnChangesPopover } from './ColumnChangesPopover';
import { ConversationAttributesCheckboxList } from './ConversationAttributesCheckboxList';

type Props = {
  className?: string;
  /** Items prepended to the transfer list (e.g. main columns group) */
  prependItems?: TransferListItem[];
  /** Items appended after attribute groups (e.g. expert reports section) */
  appendItems?: TransferListItem[];
  /**
   * Текущие выбранные ID. Если передан `onSelectionChange` — компонент работает в controlled-режиме
   * (selectedPaths используется как «живое» состояние). Иначе — это начальное состояние, после чего
   * компонент управляет локальной копией внутри себя.
   */
  selectedPaths: string[];
  /**
   * Колбэк изменения выбранных ID. Передача этого пропа переводит компонент в controlled-режим:
   * каждое изменение в Transfer/чекбокс-листе тут же поднимается наружу, а кнопки Save/Reset
   * работают с актуальным значением `selectedPaths`.
   */
  onSelectionChange?: (paths: string[]) => void;
  /**
   * Базовое состояние для расчёта изменений (added/removed). По умолчанию — `selectedPaths` на момент монтирования.
   * Используется в controlled-режиме, чтобы сравнение «текущее vs изначальное» оставалось корректным.
   */
  initialSelectedPaths?: string[];
  /** Callback when save button is clicked - receives current local state */
  onSave: (paths: string[]) => void;
  /** Optional callback for reset button */
  onReset?: () => void;
  /** Callback for cancel button */
  onCancel: () => void;
  /** Кастомный рендер элемента Transfer (например, для отступов и каскадных чекбоксов) */
  renderItem?: TransferProps['renderItem'];
  /**
   * Хук для трансформации ID при выборе. Получает ID кликнутого элемента, новый список ID
   * и предыдущий — возвращает финальный список (например, после применения каскадного выделения).
   */
  transformOnSelect?: (clickedId: TransferAllowedId, nextIds: string[], prevIds: string[]) => string[];
  /** Колбэк раскрытия/сворачивания группы Transfer (используется для ленивой загрузки) */
  onExpandGroup?: TransferProps['onExpandGroup'];
};

export const ConversationAttributesSettings: FC<Props> = ({
  className,
  prependItems,
  appendItems,
  selectedPaths,
  onSelectionChange,
  initialSelectedPaths,
  onSave,
  onReset,
  onCancel,
  renderItem,
  transformOnSelect,
  onExpandGroup,
}) => {
  const { t } = useTranslation(['dialogs', 'common']);
  const { isMobile } = useMainLayout();
  const { data: attributesData, isLoading: isLoadingAttributes } = useConversationsAttributes({
    limit: 1000,
    'is-deleted': false,
  });

  const isControlled = !!onSelectionChange;

  // Локальная копия используется только в uncontrolled-режиме
  const [internalSelectedPaths, setInternalSelectedPaths] = useState<string[]>(selectedPaths);
  const [cache, setCache] = useState(+new Date());

  const updateCache = () => {
    setCache(+new Date());
  };

  // В uncontrolled-режиме синхронизируем локальное состояние с пропсом-инициализатором
  useEffect(() => {
    if (!isControlled) setInternalSelectedPaths(selectedPaths);
  }, [selectedPaths, isControlled]);

  // Базовое состояние для расчёта изменений (по умолчанию — selectedPaths на момент монтирования)
  const baselineSelectedPathsRef = useMemo(
    () => initialSelectedPaths ?? selectedPaths,
    // Перерасчёт только когда явно меняется initialSelectedPaths (для случаев, когда диалог пересоздаётся)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialSelectedPaths],
  );

  const localSelectedPaths = isControlled ? selectedPaths : internalSelectedPaths;
  const updateSelectedPaths = (next: string[]) => {
    if (isControlled) {
      onSelectionChange?.(next);
      return;
    }
    setInternalSelectedPaths(next);
  };

  const attributesByCode = useMemo(() => {
    const map = new Map<string, ConversationAttributeItem>();

    attributesData?.attributes?.forEach((attribute) => {
      map.set(attribute.code, attribute);
    });

    return map;
  }, [attributesData]);

  const transferItems: TransferListItem[] = useMemo(() => {
    const groupsMap = new Map<string | typeof NO_GROUP_KEY, TransferListItem>();

    const ensureGroup = (groupCode: string | typeof NO_GROUP_KEY, groupName?: string) => {
      if (!groupsMap.has(groupCode)) {
        const label = groupCode === NO_GROUP_KEY ? t('dialogs:noGroup') : groupName || groupCode;

        groupsMap.set(groupCode, {
          id: groupCode,
          label: label,
          children: [],
        });
      }

      return groupsMap.get(groupCode)!;
    };

    const attributesList = attributesData?.attributes ?? [];

    attributesList.forEach((attr) => {
      const attribute = attributesByCode.get(attr.code);
      const attributeGroups = attribute?.attributeGroups;

      if (attributeGroups && attributeGroups.length > 0) {
        attributeGroups.forEach((group) => {
          const groupItem = ensureGroup(group.code, group.name);

          groupItem.children!.push({
            id: attr.path,
            label: attr.name,
          });
        });
      } else {
        const groupItem = ensureGroup(NO_GROUP_KEY);

        groupItem.children!.push({
          id: attr.path,
          label: attr.name,
        });
      }
    });

    return [...groupsMap.values()];
  }, [attributesByCode, attributesData, t]);

  const allItems: TransferListItem[] = useMemo(
    () => [...(prependItems ?? []), ...transferItems, ...(appendItems ?? [])],
    [prependItems, transferItems, appendItems],
  );

  // Create map for quick lookup of items by id
  const itemsByIdMap = useMemo(() => {
    const map = new Map<string, TransferListItem>();

    allItems.forEach((group) => {
      group.children?.forEach((child) => {
        map.set(String(child.id), child);
      });
    });

    return map;
  }, [allItems]);

  // Compute changes with full TransferItem objects
  const changes = useMemo(() => {
    const baseline = baselineSelectedPathsRef;
    const initialSet = new Set(baseline.map(String));
    const localSet = new Set(localSelectedPaths.map(String));

    const addedIds = localSelectedPaths.filter((id) => !initialSet.has(String(id)));
    const removedIds = baseline.filter((id) => !localSet.has(String(id)));

    const filterItems = (ids: string[]) => {
      return allItems.reduce<TransferListItem[]>((acc, item) => {
        if (!item.children && ids.includes(item.id.toString())) {
          acc.push(item);
        } else if (item.children?.length) {
          const filtred = item.children.filter((child) => ids.includes(child.id.toString()));
          if (filtred.length) {
            acc.push({
              ...item,
              children: filtred,
            });
          }
        }
        return acc;
      }, []);
    };
    const calculate = (items: TransferListItem[]) => {
      return items.reduce((acc, item) => {
        if (!item.children?.length) {
          acc += 1;
        } else {
          acc += calculate(item.children);
        }
        return acc;
      }, 0);
    };

    const added: TransferListItem[] = filterItems(addedIds);

    const removed: TransferListItem[] = filterItems(removedIds);

    return { added, removed, totalCount: calculate(added) + calculate(added) };
  }, [baselineSelectedPathsRef, localSelectedPaths, itemsByIdMap]);

  const handleSave = () => {
    onSave(localSelectedPaths);
  };

  const handleReset = () => {
    updateSelectedPaths(baselineSelectedPathsRef);
    updateCache();
    onReset?.();
  };

  return (
    <div className={cn('flex flex-col overflow-hidden', className)}>
      <AwaitContainer className="flex-1 flex flex-col overflow-hidden">
        {isLoadingAttributes ? (
          <div className="flex justify-center py-2">
            <Spinner className="size-4" />
          </div>
        ) : isMobile ? (
          <ConversationAttributesCheckboxList
            items={allItems}
            selectedIds={localSelectedPaths}
            onSelectionChange={updateSelectedPaths}
            renderItem={renderItem}
          />
        ) : (
          <Transfer
            checkedResetKey={cache}
            className="flex-1 h-full"
            items={allItems}
            defaultOpened={[false, true]}
            rightIds={localSelectedPaths}
            onSelect={(clickedId, nextSelectedIds) => {
              const next = nextSelectedIds as string[];
              const finalIds = transformOnSelect ? transformOnSelect(clickedId, next, localSelectedPaths) : next;
              updateSelectedPaths(finalIds);
            }}
            renderItem={renderItem}
            onExpandGroup={onExpandGroup}
            listTitles={[t('dialogs:availableForDisplay'), t('dialogs:attributesToTable')]}
          />
        )}
      </AwaitContainer>

      <footer className="flex flex-col gap-3 border-t pt-4 mt-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {changes.totalCount === 0 ? (
            t('dialogs:attributeSettingsNoChanges')
          ) : (
            <ColumnChangesPopover added={changes.added} removed={changes.removed} totalCount={changes.totalCount} />
          )}
        </div>
        <div className="flex gap-3 justify-end">
          {isMobile ? (
            <Tooltip tooltip={t('dialogs:attributeSettingsReset')}>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('dialogs:attributeSettingsReset')}
                onClick={handleReset}
              >
                <RotateCcwIcon className="size-4" />
              </Button>
            </Tooltip>
          ) : (
            <Button variant="ghost" onClick={handleReset}>
              {t('dialogs:attributeSettingsReset')}
            </Button>
          )}
          <Button variant="ghost" onClick={onCancel}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleSave}>{t('common:accept')}</Button>
        </div>
      </footer>
    </div>
  );
};
